import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../../../app/data/client';
import Logger from '../../../../../app/shared/Logger';
import type { PricelistProductAccessoryData, PricingContext } from '../../../../model';
import { GetProductModelAccessoriesQuery } from '../../../../queries';

type LoadingStatus = 'idle' | 'pending' | 'error' | 'success';

export default class ExpandableProductAccessoriesState {
  @observable.ref
  public items: PricelistProductAccessoryData[] = [];

  @observable
  public loadingStatus: LoadingStatus = 'idle';

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    private readonly productId: string,
    public readonly total: number,
  ) {
    makeObservable(this);
  }

  @action.bound
  public onExpandedChange(isExpanded: boolean): void {
    if (!isExpanded || this.loadingStatus !== 'idle') {
      return;
    }

    this.load();
  }

  private async load(): Promise<void> {
    this.setLoadingStatus('pending');

    try {
      const { accessories } = await this.client.send(
        new GetProductModelAccessoriesQuery({ pricingContext: this.pricingContext, productId: this.productId }),
      );

      this.setItems(accessories);
      this.setLoadingStatus('success');
    } catch (error) {
      Logger.error(`Unable to load the accessories. Product ID: ${this.productId}. Error: ${error}`);
      this.setLoadingStatus('error');
    }
  }

  @action
  private setItems(items: PricelistProductAccessoryData[]): void {
    this.items = items;
  }

  @action
  private setLoadingStatus(loadingStatus: LoadingStatus): void {
    this.loadingStatus = loadingStatus;
  }
}
